<template>
  <div class="card-compacta" :style="{ '--accent': color }" @click="$emit('detalle')">
    <div class="cc-accent" :style="{ background: color }"></div>

    <!-- Encabezado: máquina + estado -->
    <div class="cc-header">
      <div class="cc-maq-block">
        <span class="cc-maq-label">Máquina</span>
        <span class="cc-maq-code">{{ item.codigoMaquina || '—' }}</span>
      </div>
      <div class="cc-status" :style="statusStyle">
        <span class="cc-status-dot" :style="{ background: color }"></span>
        {{ estadoLabel }}
      </div>
    </div>

    <!-- Molde -->
    <div class="cc-molde">
      <span class="cc-molde-label">Molde</span>
      <span class="cc-molde-value">{{ item.codigoMolde || '—' }}</span>
    </div>

    <!-- Artículos / cavidades -->
    <div v-if="cavidades.length" class="cc-arts">
      <div v-for="(ca, i) in cavidades" :key="'cc-art-' + i" class="cc-art-row">
        <span class="cc-art-dot" :style="{ background: color }"></span>
        <span class="cc-art-name">{{ ca.nombre }}{{ sufijoLado(ca.lado) }}</span>
        <span class="cc-art-cav">{{ ca.cavidades }} {{ ca.cavidades === 1 ? 'cav.' : 'cav.' }}</span>
      </div>
    </div>
    <div v-else class="cc-arts cc-arts--empty">Sin artículos</div>

    <!-- Causa de paro: solo cuando la máquina está en paro. En neutro (gris y negro): el tipo de
         paro ya lo comunica el borde de la tarjeta. -->
    <div v-if="enParo" class="cc-paro">
      <span class="cc-paro-value">{{ causaParo }}</span>
    </div>
  </div>
</template>

<script>
const STATUS_MAP = {
  1: { label: 'Activa',          color: '#2B7EFF' },
  2: { label: 'Sin OT',          color: '#8A95A3' },
  3: { label: 'Paro Programado', color: '#FF9F00' },
  4: { label: 'Paro No Prog.',   color: '#E83535' },
};

export default {
  name: 'CardMaquinaCompacta',

  props: {
    item: { type: Object, default: () => ({}) },
    // Nombre configurable del paro programado (status 3). Debe coincidir con el de la pestaña.
    nombreParoProg: { type: String, default: 'Paro Programado' },
    // Configuración "Agregar LH y RH": añade el lado después del código del artículo.
    ladoLhRh: { type: Boolean, default: false },
  },

  methods: {
    // El adaptador determina el lado exclusivamente desde la descripción del artículo.
    // Si no hubo una coincidencia inequívoca, `lado` llega vacío y se deja solo el código.
    sufijoLado(lado) {
      if (!this.ladoLhRh || (lado !== 'LH' && lado !== 'RH')) return '';
      return ` ${lado}`;
    },
  },

  computed: {
    color() {
      return (STATUS_MAP[this.item.status] || { color: '#8A95A3' }).color;
    },

    estadoLabel() {
      if (this.item.activaSinOt) return 'Activa sin OT';
      if (this.item.otSinGolpes) return 'OT sin golpes';
      if (this.item.status === 3) return this.nombreParoProg;
      return (STATUS_MAP[this.item.status] || { label: 'Sin OT' }).label;
    },

    statusStyle() {
      const c = this.color;
      return { background: c + '15', color: c, borderColor: c + '40' };
    },

    cavidades() {
      return Array.isArray(this.item.cavidadesArticulos) ? this.item.cavidadesArticulos : [];
    },

    // Paro programado (3) o no programado (4).
    enParo() {
      return this.item.status === 3 || this.item.status === 4;
    },

    // Causa del paro: tipo · subtipo (el subtipo puede no venir).
    causaParo() {
      const partes = [this.item.paroTipo, this.item.paroSubTipo].filter(Boolean);
      return partes.length ? partes.join(' · ') : 'Sin especificar';
    },
  },
};
</script>

<style scoped>
.card-compacta {
  position: relative;
  width: 100%;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 16px;
  padding: 14px 16px 12px 18px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: box-shadow 0.18s, transform 0.18s, border-color 0.18s;
  font-family: 'Space Grotesk', 'Roboto', sans-serif;
  overflow: hidden;
}

.card-compacta:hover {
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  border-color: rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.card-compacta:active {
  transform: translateY(0);
}

.cc-accent {
  position: absolute;
  top: 0;
  left: 0;
  width: 5px;
  height: 100%;
  border-radius: 16px 0 0 16px;
}

/* Encabezado */
.cc-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.cc-maq-block {
  display: flex;
  flex-direction: column;
  line-height: 1.1;
}

.cc-maq-label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #8A95A3;
}

.cc-maq-code {
  font-size: 22px;
  font-weight: 700;
  color: #2B7EFF;
}

.cc-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 20px;
  border: 1px solid transparent;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.cc-status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* Molde. Grid de 3 columnas (1fr auto 1fr): la etiqueta va a la izquierda y el código queda
   centrado en la fila completa, no en el espacio que sobra a la derecha de la etiqueta. */
.cc-molde {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 8px 12px;
  border-radius: 10px;
  background: #F3F5F9;
}

.cc-molde-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #8A95A3;
}

.cc-molde-value {
  grid-column: 2;
  text-align: center;
  font-size: 18px;
  font-weight: 700;
  color: #0D1117;
}

/* Artículos */
.cc-arts {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.cc-arts--empty {
  font-size: 12px;
  color: #B4BCC7;
  font-style: italic;
}

.cc-art-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cc-art-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  flex-shrink: 0;
  opacity: 0.6;
}

.cc-art-name {
  flex: 1 1 auto;
  font-size: 11.5px;
  font-weight: 700;
  color: #8A95A3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cc-art-cav {
  flex: 0 0 auto;
  padding: 1px 7px;
  border-radius: 8px;
  background: #F3F5F9;
  font-size: 10px;
  font-weight: 700;
  color: #8A95A3;
}

/* Footer causa de paro: mismo gris neutro que el molde, texto en negro. */
.cc-paro {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 12px;
  padding: 8px 12px;
  border-radius: 10px;
  background: #F3F5F9;
}

.cc-paro-value {
  font-size: 12px;
  font-weight: 500;
  color: #0D1117;
  line-height: 1.25;
  /* La causa puede ser larga: hasta 2 líneas y luego ellipsis. */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
